import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import type { HourlyData } from './PulseTimeline';
import { MOCK_HOURLY_DATA } from './PulseTimeline';

export type TimeRingHeatmapProps = {
  data: HourlyData[];
  selectedHour?: number;
  onHourChange?: (hour: number) => void;
  isLoading?: boolean;
};

type TooltipState = {
  x: number;
  y: number;
  hour: number;
  value: number;
  avg: number;
  visible: boolean;
};

function formatHourRange(h: number): string {
  const fmt = (n: number) => {
    if (n === 0) return '12 AM';
    if (n < 12) return `${n} AM`;
    if (n === 12) return '12 PM';
    return `${n - 12} PM`;
  };
  return `${fmt(h)} – ${fmt((h + 1) % 24)}`;
}

// Green → yellow → red interpolator
const occupancyColor = (t: number, alpha = 0.9, isLoading = false): string => {
  if (isLoading) return `rgba(161, 161, 170, ${0.1 + t * 0.3})`;
  let r, g, b;
  if (t < 0.5) {
    const s = t * 2;
    r = Math.round(34 + s * (234 - 34));
    g = Math.round(197 + s * (179 - 197));
    b = Math.round(94 + s * (8 - 94));
  } else {
    const s = (t - 0.5) * 2;
    r = Math.round(234 + s * (239 - 234));
    g = Math.round(179 + s * (68 - 179));
    b = Math.round(8 + s * (68 - 8));
  }
  return `rgba(${r},${g},${b},${alpha})`;
}

// Clock labels at cardinal positions (3h interval)
const CLOCK_LABELS = [
  { hour: 0, label: '12', angle: -Math.PI / 2 },
  { hour: 6, label: '6', angle: 0 },
  { hour: 12, label: '12', angle: Math.PI / 2 },
  { hour: 18, label: '18', angle: Math.PI },
];

export default function TimeRingHeatmap({ data, selectedHour, onHourChange, isLoading = false }: TimeRingHeatmapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState>({ x: 0, y: 0, hour: 0, value: 0, avg: 0, visible: false });
  const [size, setSize] = useState(280);

  // Mock data is only a loading skeleton — never a stand-in for real "today"
  // numbers. Once loaded with no check-ins we show an honest empty state.
  const displayData = useMemo(() => (isLoading ? MOCK_HOURLY_DATA : data), [data, isLoading]);
  const isEmpty = !isLoading && data.length === 0;
  const maxVal = useMemo(() => Math.max(...displayData.map(d => d.value), 1), [displayData]);
  const avgVal = useMemo(() => displayData.reduce((sum, d) => sum + d.value, 0) / 24, [displayData]);
  const peakEntry = useMemo(() => displayData.reduce((best, d) => d.value > best.value ? d : best, { hour: 0, value: 0 }), [displayData]);

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w) setSize(Math.min(Math.max(w, 200), 360));
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const draw = useCallback(() => {
    if (!svgRef.current) return;

    const cx = size / 2;
    const cy = size / 2;
    const outerR = size * 0.42;
    const innerR = size * 0.24;
    const expandR = outerR + size * 0.04;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', size).attr('height', size);

    const defs = svg.append('defs');

    // Glow filter for high-intensity segments
    const glow = defs.append('filter').attr('id', 'trh-glow').attr('x', '-30%').attr('y', '-30%').attr('width', '160%').attr('height', '160%');
    glow.append('feGaussianBlur').attr('stdDeviation', '4').attr('result', 'blur');
    const feMerge = glow.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'blur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    const g = svg.append('g').attr('transform', `translate(${cx},${cy})`);

    // Background ring (track)
    g.append('circle')
      .attr('r', (innerR + outerR) / 2)
      .attr('fill', 'none')
      .attr('stroke', 'rgba(0,0,0,0.05)')
      .attr('stroke-width', outerR - innerR);

    // 24 arc segments
    const segmentAngle = (2 * Math.PI) / 24;
    const padAngle = 0.025;

    displayData.forEach((d, _i) => {
      const norm = maxVal > 0 ? d.value / maxVal : 0;
      // Hours start at midnight (0) = top, going clockwise
      const startAngle = (d.hour / 24) * 2 * Math.PI - Math.PI / 2;
      const endAngle = startAngle + segmentAngle;
      const isSelected = selectedHour === d.hour;
      const isHigh = norm > 0.7;

      const arc = d3.arc()
        .innerRadius(innerR)
        .outerRadius(isSelected ? expandR : outerR)
        .startAngle(startAngle + padAngle / 2)
        .endAngle(endAngle - padAngle / 2)
        .cornerRadius(3);

      const opacity = norm < 0.05 ? 0.15 : 0.7 + norm * 0.3;
      const color = occupancyColor(norm, opacity, isLoading);

      const segGroup = g.append('g')
        .attr('class', `seg-${d.hour}`)
        .style('cursor', 'pointer');

      const path = segGroup.append('path')
        .attr('d', arc as any)
        .attr('fill', norm < 0.05 ? 'rgba(0,0,0,0.05)' : color)
        .attr('stroke', 'transparent')
        .attr('stroke-width', 1);

      if (isSelected) {
        path.attr('stroke', '#AAD816').attr('stroke-width', 1.5).attr('opacity', 1);
      }

      if (isHigh && d.value > 0) {
        path.attr('filter', 'url(#trh-glow)');
      }

      // Hover interaction
      segGroup
        .on('mouseenter', function(event) {
          if (selectedHour !== d.hour) {
            const hoverArc = d3.arc()
              .innerRadius(innerR)
              .outerRadius(expandR)
              .startAngle(startAngle + padAngle / 2)
              .endAngle(endAngle - padAngle / 2)
              .cornerRadius(3);
            d3.select(this).select('path')
              .transition().duration(150)
              .attr('d', hoverArc as any)
              .attr('opacity', 1);
          }

          const svgRect = svgRef.current?.getBoundingClientRect();
          if (svgRect) {
            setTooltip({
              x: event.clientX - svgRect.left,
              y: event.clientY - svgRect.top - 10,
              hour: d.hour,
              value: d.value,
              avg: Math.round(avgVal),
              visible: true
            });
          }
          // Removed onHourChange(d.hour) to prevent re-renders on hover
        })
        .on('mouseleave', function() {
          if (selectedHour !== d.hour) {
            const origArc = d3.arc()
              .innerRadius(innerR)
              .outerRadius(outerR)
              .startAngle(startAngle + padAngle / 2)
              .endAngle(endAngle - padAngle / 2)
              .cornerRadius(3);
            d3.select(this).select('path')
              .transition().duration(150)
              .attr('d', origArc as any)
              .attr('opacity', opacity);
          }
          setTooltip(prev => ({ ...prev, visible: false }));
        });

      // Animate in with staggered opacity
      path
        .attr('opacity', 0)
        .transition()
        .delay(d.hour * 25)
        .duration(400)
        .attr('opacity', isSelected ? 1 : opacity);
    });

    // Clock hour labels (4 cardinal points)
    const labelR = outerR + size * 0.09;
    CLOCK_LABELS.forEach(({ label, angle }) => {
      g.append('text')
        .attr('x', Math.cos(angle) * labelR)
        .attr('y', Math.sin(angle) * labelR)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', 'rgba(0,0,0,0.3)')
        .attr('font-size', `${size * 0.028}px`)
        .attr('font-weight', '700')
        .attr('font-family', 'inherit')
        .text(label);
    });

    // Tick marks at each hour (small dots on outer ring)
    for (let h = 0; h < 24; h++) {
      const angle = (h / 24) * 2 * Math.PI - Math.PI / 2;
      const tickR = outerR + size * 0.03;
      const isMajor = h % 6 === 0;
      g.append('circle')
        .attr('cx', Math.cos(angle) * tickR)
        .attr('cy', Math.sin(angle) * tickR)
        .attr('r', isMajor ? 2 : 1)
        .attr('fill', isMajor ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.05)');
    }

    // Inner circle content
    g.append('circle')
      .attr('r', innerR - 2)
      .attr('fill', 'rgba(0,0,0,0.02)');

    // Peak label
    g.append('text')
      .attr('y', -size * 0.04)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgba(0,0,0,0.3)')
      .attr('font-size', `${size * 0.032}px`)
      .attr('font-weight', '700')
      .attr('letter-spacing', '0.05em')
      .text('PEAK');

    const peakLabel = peakEntry.value > 0
      ? (peakEntry.hour === 12 ? '12 PM' : peakEntry.hour < 12 ? `${peakEntry.hour} AM` : `${peakEntry.hour - 12} PM`)
      : '—';

    g.append('text')
      .attr('y', size * 0.035)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('font-size', `${size * 0.065}px`)
      .attr('font-weight', '900')
      .text(peakLabel);

    if (peakEntry.value > 0) {
      g.append('text')
        .attr('y', size * 0.1)
        .attr('text-anchor', 'middle')
        .attr('fill', 'rgba(0,0,0,0.3)')
        .attr('font-size', `${size * 0.028}px`)
        .attr('font-weight', '600')
        .text(`${peakEntry.value} check-ins`);
    }

  }, [displayData, size, maxVal, avgVal, peakEntry, selectedHour, onHourChange, isLoading]);

  useEffect(() => { draw(); }, [draw]);

  const tooltipColor = tooltip.value > tooltip.avg * 1.5
    ? '#ef4444'
    : tooltip.value > tooltip.avg
      ? '#eab308'
      : '#22c55e';

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-10">
        <p className="text-sm font-black text-zinc-400 uppercase tracking-wider">No check-ins yet today</p>
        <p className="text-[10px] text-zinc-300 font-bold mt-1">The activity clock fills in as members check in</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full select-none flex flex-col items-center">
      <svg ref={svgRef} className="overflow-visible" />

      {/* Legend */}
      <div className="flex items-center gap-3 mt-2">
        {[
          { label: 'Low', color: occupancyColor(0.1, 0.8, isLoading) },
          { label: 'Med', color: occupancyColor(0.5, 0.8, isLoading) },
          { label: 'High', color: occupancyColor(0.9, 0.8, isLoading) }
        ].map(({ label, color }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest">{label}</span>
          </div>
        ))}
      </div>

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          className="absolute z-20 pointer-events-none"
          style={{ left: tooltip.x, top: tooltip.y - 60, transform: 'translateX(-50%)' }}
        >
          <div className="bg-white/95 border border-zinc-200 rounded-xl px-3 py-2 shadow-2xl text-center min-w-[120px] backdrop-blur-md">
            <div className="text-zinc-500 text-[9px] font-bold uppercase tracking-widest mb-1">{formatHourRange(tooltip.hour)}</div>
            <div className="text-zinc-950 font-black text-sm tabular-nums">{tooltip.value} check-ins</div>
            <div className="text-[10px] font-bold mt-0.5" style={{ color: tooltipColor }}>
              {tooltip.avg > 0
                ? tooltip.value > tooltip.avg
                  ? `+${Math.round(((tooltip.value - tooltip.avg) / tooltip.avg) * 100)}% vs avg`
                  : `${Math.round(((tooltip.value - tooltip.avg) / (tooltip.avg || 1)) * 100)}% vs avg`
                : 'No baseline'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
